import { computed, onMounted, ref, watch } from 'vue';
import {
  activeProjectId,
  fetchActiveProjects,
  invalidateProjectsCache,
  setActiveProjectId,
  type Project,
} from '../api';

const STORAGE_KEY = 'cdp:selectedProjectId';

const projects = ref<Project[]>([]);
const loading = ref(false);
let initialized = false;

export function useCdpProjectContext() {
  const selectedProject = computed(() =>
    projects.value.find((p) => p.id === activeProjectId.value) ?? null,
  );

  const projectOptions = computed(() =>
    projects.value.map((p) => ({ value: p.id, text: p.name })),
  );

  async function loadProjects(): Promise<void> {
    loading.value = true;
    try {
      invalidateProjectsCache();
      projects.value = await fetchActiveProjects();
      if (activeProjectId.value) {
        const exists = projects.value.some((p) => p.id === activeProjectId.value);
        if (!exists) setActiveProjectId(null);
      }
      if (!activeProjectId.value && projects.value.length > 0) {
        const saved = localStorage.getItem(STORAGE_KEY);
        const match = saved ? projects.value.find((p) => p.id === saved) : null;
        setActiveProjectId(match?.id ?? projects.value[0].id);
      }
    } finally {
      loading.value = false;
    }
  }

  function selectProject(id: string | null): void {
    setActiveProjectId(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  }

  onMounted(() => {
    if (initialized) return;
    initialized = true;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) setActiveProjectId(saved);
    void loadProjects();
  });

  watch(activeProjectId, (id) => {
    if (id) localStorage.setItem(STORAGE_KEY, id);
  });

  return {
    projects,
    loading,
    selectedProjectId: activeProjectId,
    selectedProject,
    projectOptions,
    loadProjects,
    selectProject,
    hasProject: computed(() => !!activeProjectId.value),
  };
}
